// 制御面 WS（/v1/control・常時＝§4.1「制御の背骨」・設計v2 §2.3 / AC-20）。
// アプリ常駐・自動再接続（指数 backoff 1s→30s）・25s ping keepalive。
// VS は konecco 下りイベントを全接続へ broadcast する（接続単位ルーティングなし）ので、
// 購読側（call-store）が conversationId で自前フィルタする。

import { voiceWsUrl } from './voice-config';
import type { ControlEvent } from './voice-types';

type Listener = (ev: ControlEvent) => void;
type StatusListener = (connected: boolean) => void;

const PING_INTERVAL_MS = 25_000;
const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

class ControlChannel {
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private backoff = BACKOFF_MIN_MS;
  private pingTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private started = false;
  // 現行 WS と世代 id。再接続の過渡で複数 WS が生じても、非現行 WS の
  // onopen/onclose/onmessage を無視し、stale な onclose が setConnected(false) を
  // 呼び戻して voiceOnline を落とす競合を防ぐ（AC-20 再接続バグ・QA で発見）。
  private ws: WebSocket | null = null;
  private gen = 0;
  connected = false;

  start(): void {
    if (this.started) return;
    this.started = true;
    this.connect();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  onStatus(fn: StatusListener): () => void {
    this.statusListeners.add(fn);
    fn(this.connected);
    return () => this.statusListeners.delete(fn);
  }

  private setConnected(v: boolean): void {
    if (this.connected === v) return;
    this.connected = v;
    for (const fn of this.statusListeners) fn(v);
  }

  private clearPing(): void {
    if (this.pingTimer != null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private connect(): void {
    // 既存 WS があれば現行から切り離して閉じる（handler は gen 不一致で無視される）。
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
    }
    this.clearPing();
    const myGen = ++this.gen;
    let ws: WebSocket;
    try {
      ws = new WebSocket(voiceWsUrl('/v1/control'));
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (myGen !== this.gen) return; // stale
      this.backoff = BACKOFF_MIN_MS;
      this.setConnected(true);
      this.clearPing();
      this.pingTimer = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
      }, PING_INTERVAL_MS);
    };
    ws.onmessage = (e) => {
      if (myGen !== this.gen) return; // stale
      let ev: ControlEvent;
      try {
        ev = JSON.parse(String(e.data)) as ControlEvent;
      } catch {
        return;
      }
      if (!ev || typeof ev !== 'object' || typeof (ev as { type?: unknown }).type !== 'string') return;
      for (const fn of this.listeners) fn(ev);
    };
    ws.onclose = () => {
      if (myGen !== this.gen) return; // 非現行 WS の遅延 close は無視（voiceOnline を落とさない）
      this.setConnected(false);
      this.clearPing();
      this.ws = null;
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      /* onclose で再接続する */
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer != null) return;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.backoff);
    this.backoff = Math.min(this.backoff * 2, BACKOFF_MAX_MS);
  }
}

/** アプリで 1 本のシングルトン */
export const controlChannel = new ControlChannel();
